import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import moment from 'moment';
import { tap } from 'rxjs/operators';
import { emailContactToString, emailStringToContact } from 'src/app/helper/emailingHelpers';
import { NotificationType } from 'src/app/samwebDescription/samweb/notification/notificationType';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { EmailPurpose } from 'src/app/samwebDescription/samwin/EmailPurpose';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { ToastUserInfoNotification } from 'src/app/store/notification/notification-toastr/notification-toastr.actions';
import { LongRunningContextStarted } from 'src/app/store/server-communication/running-actions/running-actions.actions';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { SessionState } from 'src/app/store/session/session.state';
import { RemoveWindow } from 'src/app/store/utility/samweb-window/samweb-window.actions';
import { WindowTypes } from 'src/app/store/utility/samweb-window/samweb-window.state';
import { CallTagsForSingleCallRemoteDto } from 'src/generated/signalR/call-tags-for-single-call-remote-dto';
import { EmailTemplateShortInfoRemoteDto } from 'src/generated/signalR/email-template-short-info-remote-dto';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { AttachmentRemoteDto, DirectoryEntryRemoteDto, EmailTemplateResponse, EmailsService } from 'src/generated/swagger';
import { EmailTemplateRemoteDto } from 'src/generated/swagger/model/emailTemplateRemoteDto';
import { CallTagsSelectors } from '../../call-tags/call-tags.selectors';
import { SelectedSubscriberState } from '../../directory/selected-subscriber/selected-subscriber.state';
import { SamwinLinesStateSelector } from '../../samwin-lines/samwin-lines.selector';
import { SamwinSkillsSelectors } from '../../samwin-skills/samwin-skills.selectors';
import { SamwinSkillsState } from '../../samwin-skills/samwin-skills.state';
import { Email } from '../email-behaviour/email';
import { ClearAttachments, DeleteEmailBehaviourState, EmailCreate, EmailReset } from '../email-behaviour/email-behaviour.actions';
import { EmailBehaviourStateSelectors } from '../email-behaviour/email-behaviour.selectors';
import { EmailBehaviourState } from '../email-behaviour/email-behaviour.state';
import { EmailContact } from '../email-behaviour/email-contact';
import { EmailTemplateState } from '../email-template/email-template.state';
import {
   ChangeEditorToReader,
   ChangeReaderToEditor,
   EmailTemplateSelected,
   EndEmailProcess,
   SendEmailProcess,
   StartEmailProcess,
} from './email-dialog-behaviour.actions';

export interface EmailDialogBehaviourStateModel {
   [messageId: string]: EmailDialogBehaviourStateInstance;
}

export interface ReferencedEmail {
   messageId: string;
   timeStamp: Date;
   email: Email;
   attachments: { [id: string]: AttachmentRemoteDto };
}

export interface EmailDialogBehaviourStateInstance {
   messageId: string;
   purpose: EmailPurpose;
   searchText: string;
   contextData: any;
   selectableTemplates: EmailTemplateShortInfoRemoteDto[];
   selectedTemplate: EmailTemplateRemoteDto;
   callTags: CallTagsForSingleCallRemoteDto;
   selectedSubscriber: DirectoryEntryRemoteDto;
   callerInfo: SamwinCallInfoRemoteDto;
   requestingSkill: number;
   referencedEmail: ReferencedEmail;
}

export function DefaultEmailDialogBehaviourInstance(): EmailDialogBehaviourStateInstance {
   return {
      messageId: null,
      purpose: null,
      searchText: '',
      contextData: null,
      selectableTemplates: null,
      selectedTemplate: null,
      callTags: null,
      selectedSubscriber: null,
      callerInfo: null,
      requestingSkill: null,
      referencedEmail: null,
   };
}

@State<EmailDialogBehaviourStateModel>({
   name: 'emailDialogBehaviourState',
   defaults: {},
})
@Injectable()
export class EmailDialogBehaviourState {
   emailCount: number;

   constructor(private store: Store, private emailsService: EmailsService, private translateService: TranslateService) {
      this.emailCount = 0;
   }

   private static getSpecificBehaviourStateOrDefault(state: EmailDialogBehaviourStateModel, messageId: string): EmailDialogBehaviourStateInstance {
      const value = state[messageId];
      if (value) {
         return value;
      } else {
         return DefaultEmailDialogBehaviourInstance();
      }
   }

   private createEmailFromTemplate(currentState: EmailDialogBehaviourStateInstance): Email {
      const isReply: boolean = currentState.purpose.name === EmailPurpose.REPLY.name;
      const isReplyAll: boolean = currentState.purpose.name === EmailPurpose.REPLY_ALL.name;
      const isForward: boolean = currentState.purpose.name === EmailPurpose.FORWARD.name;

      const getReplaceAllTagsInText = this.store.selectSnapshot(CallTagsSelectors.getReplaceAllTagsInText);
      const from = getReplaceAllTagsInText(currentState.selectedTemplate.from, currentState.callTags, currentState.callerInfo, currentState.selectedSubscriber)
         .text;
      let to: EmailContact[] = emailStringToContact(
         getReplaceAllTagsInText(currentState.selectedTemplate.to, currentState.callTags, currentState.callerInfo, currentState.selectedSubscriber).text
      );
      let cc: EmailContact[] = emailStringToContact(
         getReplaceAllTagsInText(currentState.selectedTemplate.cc, currentState.callTags, currentState.callerInfo, currentState.selectedSubscriber).text
      );
      const bcc: EmailContact[] = emailStringToContact(
         getReplaceAllTagsInText(currentState.selectedTemplate.bcc, currentState.callTags, currentState.callerInfo, currentState.selectedSubscriber).text
      );
      let subject = getReplaceAllTagsInText(
         currentState.selectedTemplate.subject,
         currentState.callTags,
         currentState.callerInfo,
         currentState.selectedSubscriber
      ).text;
      let body = getReplaceAllTagsInText(currentState.selectedTemplate.body, currentState.callTags, currentState.callerInfo, currentState.selectedSubscriber)
         .text;

      if (isReply) {
         to = emailStringToContact(currentState.referencedEmail.email.from);
      }

      if (isReplyAll) {
         // Filter out own mail on replay all
         to = emailStringToContact(currentState.referencedEmail.email.from).concat(currentState.referencedEmail.email.to);
         cc = currentState.referencedEmail.email.cc;
      }

      if (isReply || isReplyAll) {
         if (!_.isEmpty(currentState.referencedEmail.email.subject)) {
            subject = `${this.translateService.instant('samwin.email_dialog.reply')} ${currentState.referencedEmail.email.subject}`;
         }
      }
      if (isForward) {
         if (!_.isEmpty(currentState.referencedEmail.email.subject)) {
            subject = `${this.translateService.instant('samwin.email_dialog.forward')} ${currentState.referencedEmail.email.subject}`;
         }
      }
      if (isReply || isReplyAll || isForward) {
         const htmlTextBlock = this.getMailInfoHtmlString(currentState.referencedEmail.email, currentState.referencedEmail.timeStamp);

         body += htmlTextBlock + currentState.referencedEmail.email.body;
      }
      // NOTE: Add content on start if empty
      if (body.length === 0) {
         body = '<p></p>';
      }
      return new Email(from, to, cc, bcc, subject, body);
   }

   private getMailInfoHtmlString(referenceMailData: Email, sentDate: Date): string {
      let htmlTextBlock = '<br/>';
      htmlTextBlock += '<div style = "border: none; border-top: solid #e1e1e1 1pt; padding: 3pt 0cm 0cm 0cm;">';
      htmlTextBlock += '<div>';
      htmlTextBlock += `<b>${this.translateService.instant('samwin.email_dialog.from')}</b>&nbsp;${referenceMailData.from}<br/>`;
      htmlTextBlock += `<b>${this.translateService.instant('samwin.email_dialog.sent')}</b>&nbsp;${moment(sentDate).format('LLL')}<br/>`;
      htmlTextBlock += `<b>${this.translateService.instant('samwin.email_dialog.to')}</b>&nbsp;${emailContactToString(referenceMailData.to, '; ')}<br/>`;
      htmlTextBlock += `<b>${this.translateService.instant('samwin.email_dialog.subject')}</b>&nbsp;${referenceMailData.subject}`;
      htmlTextBlock += '</div>';
      return htmlTextBlock;
   }

   @Action(ChangeReaderToEditor, { cancelUncompleted: true })
   @ImmutableContext()
   public changeReaderToEditor(ctx: StateContext<EmailDialogBehaviourStateModel>, action: ChangeReaderToEditor) {
      const requestingSkillId = action.skillId;
      const messageId = action.messageId;
      return action.purpose
         .emailTemplateRequest(this.store, requestingSkillId, this.emailsService, action.contextData)
         .pipe(
            tap((response: EmailTemplateShortInfoRemoteDto[]) => {
               const newSelectableTemplates = _.some(response ?? []) ? response : [this.store.selectSnapshot(EmailTemplateState.defaultEmailTemplateShortInfo)];
               let newSelectedTemplate = _(newSelectableTemplates).first();
               const defaultTemplateId = this.store.selectSnapshot(SamwinSkillsState.getDefaultTemplateID)(requestingSkillId);
               if (!_.isNil(defaultTemplateId) && newSelectableTemplates.map((temp) => temp.id).includes(defaultTemplateId)) {
                  newSelectedTemplate = newSelectableTemplates.find((q) => q.id === defaultTemplateId);
               }
               ctx.setState((state: EmailDialogBehaviourStateModel) => {
                  const behaviour = EmailDialogBehaviourState.getSpecificBehaviourStateOrDefault(state, messageId);
                  behaviour.purpose = action.purpose;
                  behaviour.contextData = action.contextData;
                  behaviour.callTags = this.store.selectSnapshot(CallTagsSelectors.callTagsForActiveOrRingingCall);
                  behaviour.selectedSubscriber = this.store.selectSnapshot(SelectedSubscriberState.selectedSubscriber);
                  behaviour.selectableTemplates = newSelectableTemplates;
                  behaviour.messageId = messageId;
                  behaviour.requestingSkill = requestingSkillId;
                  behaviour.referencedEmail = !_.isNil(action.contextData)
                     ? this.store.selectSnapshot(EmailBehaviourStateSelectors.getEmailData)(action.contextData.messageId)
                     : null;
                  state[messageId] = behaviour;
                  return state;
               });

               this.store.dispatch(new EmailTemplateSelected(newSelectedTemplate, messageId));
            })
         )
         .subscribe();
   }

   @Action(ChangeEditorToReader)
   @ImmutableContext()
   public changeEditorToReader(ctx: StateContext<EmailDialogBehaviourStateModel>, action: ChangeEditorToReader) {
      ctx.dispatch(new EmailReset(action.messageId));
      ctx.setState((state: EmailDialogBehaviourStateModel) => {
         delete state[action.messageId];
         return state;
      });
   }

   @Action(StartEmailProcess, { cancelUncompleted: true })
   @ImmutableContext()
   public startEmailProcess(ctx: StateContext<EmailDialogBehaviourStateModel>, action: StartEmailProcess) {
      if (
         !this.store.selectSnapshot(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA)) &&
         !this.store.selectSnapshot(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.USE_EMAIL_AND_TEXTING))
      ) {
         console.debug('The agent is not allowed to write emails.');
         return;
      }

      const callerInfo = this.store.selectSnapshot(SamwinLinesStateSelector.getCallerInfoFromCurrentOrPreviousCall);

      const requestingSkillId = this.store.selectSnapshot(SamwinSkillsSelectors.getRequestingSkillIdForEmail);

      if (!this.store.selectSnapshot(SamwinSkillsState.hasCurrentUserEmailConnector)(requestingSkillId)) {
         const skillName = this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationFn)(requestingSkillId)?.skillName;
         ctx.dispatch(
            new ToastUserInfoNotification(
               NotificationType.WARN,
               'notification.configuration.no_email_connector.title',
               this.translateService.instant('notification.configuration.no_email_connector.message', {
                  skillName: skillName,
               })
            )
         );
         return;
      }

      return action.purpose
         .emailTemplateRequest(this.store, requestingSkillId, this.emailsService, action.contextData)
         .pipe(
            tap((response: EmailTemplateShortInfoRemoteDto[]) => {
               const newSelectableTemplates = _.some(response ?? []) ? response : [this.store.selectSnapshot(EmailTemplateState.defaultEmailTemplateShortInfo)];
               let newSelectedTemplate = _(newSelectableTemplates).first();
               const defaultTemplateId = this.store.selectSnapshot(SamwinSkillsState.getDefaultTemplateID)(requestingSkillId);
               if (!_.isNil(defaultTemplateId) && defaultTemplateId >= 0 && newSelectableTemplates.map((temp) => temp.id).includes(defaultTemplateId)) {
                  newSelectedTemplate = newSelectableTemplates.find((q) => q.id === defaultTemplateId);
               }
               const messageId = this.emailCount - 1;
               ctx.setState((state: EmailDialogBehaviourStateModel) => {
                  const behaviour = EmailDialogBehaviourState.getSpecificBehaviourStateOrDefault(state, messageId.toString());
                  behaviour.purpose = action.purpose;
                  behaviour.contextData = action.contextData;
                  behaviour.callTags = this.store.selectSnapshot(CallTagsSelectors.callTagsForActiveOrRingingCall);
                  behaviour.selectedSubscriber = this.store.selectSnapshot(SelectedSubscriberState.selectedSubscriber);
                  behaviour.callerInfo = callerInfo;
                  behaviour.requestingSkill = requestingSkillId;
                  behaviour.selectableTemplates = newSelectableTemplates;
                  behaviour.messageId = messageId.toString();
                  this.emailCount = messageId;
                  state[messageId.toString()] = behaviour;
                  return state;
               });

               this.store.dispatch(new EmailTemplateSelected(newSelectedTemplate, this.emailCount.toString()));
            })
         )
         .subscribe();
   }

   @Action(EmailTemplateSelected, { cancelUncompleted: true })
   @ImmutableContext()
   public emailTemplateSelected(ctx: StateContext<EmailDialogBehaviourStateModel>, action: EmailTemplateSelected) {
      if (_.isNil(action.template)) {
         const behaviourState = ctx.getState();
         const state = EmailDialogBehaviourState.getSpecificBehaviourStateOrDefault(behaviourState, action.messageId);
         const skillName = this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationFn)(state?.requestingSkill ?? -1)?.skillName;
         console.warn(`No default template for skill '${skillName}' found, unable to create email`);
         this.store.dispatch(
            new ToastUserInfoNotification(
               NotificationType.WARN,
               'notification.configuration.no_default_template.title',
               this.translateService.instant('notification.configuration.no_default_template.message', {
                  skillName: skillName,
               })
            )
         );
         return;
      }
      console.log(`Selected template ${action.template.id}`);

      return this.emailsService.getEmailTemplate(action.template.id).pipe(
         tap((response: EmailTemplateResponse) => {
            const tempSelectedTemplate = _(response.emailTemplate).first();

            ctx.setState((state: EmailDialogBehaviourStateModel) => {
               const behaviour = EmailDialogBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
               behaviour.selectedTemplate = tempSelectedTemplate;

               if (behaviour.purpose.name === EmailPurpose.REPLY.name || behaviour.purpose.name === EmailPurpose.REPLY_ALL.name) {
                  ctx.dispatch(new ClearAttachments(behaviour.messageId));
               }
               ctx.dispatch(new EmailCreate(behaviour.messageId, this.createEmailFromTemplate(behaviour)));
               state[action.messageId] = behaviour;
               return state;
            });
         })
      );
   }

   @Action(SendEmailProcess, { cancelUncompleted: true })
   public SendEmailProcess(ctx: StateContext<EmailDialogBehaviourStateModel>, action: SendEmailProcess) {
      ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.EMAIL_WINDOW, action.messageId));
      const behaviourState = ctx.getState();
      const state = EmailDialogBehaviourState.getSpecificBehaviourStateOrDefault(behaviourState, action.messageId);

      const contextData = state.contextData;

      const skill = this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationFn)(state.requestingSkill);

      const user = this.store.selectSnapshot(SessionState.getAuthenticatedUser);
      const connectorId = user.emailConnectorId > 0 ? user.emailConnectorId : skill.emailConnectorId;

      const email = this.store.selectSnapshot(EmailBehaviourState.getEmail)(state.messageId);
      const emailAttachments = this.store.selectSnapshot(EmailBehaviourState.getAttachments)(state.messageId);

      let attachments: AttachmentRemoteDto[] = [];
      if (!_.isNil(emailAttachments)) {
         attachments = _(Object.keys(emailAttachments).map((key) => emailAttachments[key])).value();
      }

      if (_.isNil(email)) {
         console.log('Cannot complete Email process, no content.');
         return;
      }

      const newTemplate = _.cloneDeep(state.selectedTemplate);
      newTemplate.to = emailContactToString(email.to);
      newTemplate.cc = emailContactToString(email.cc);
      newTemplate.bcc = emailContactToString(email.bcc);
      newTemplate.from = email.from;
      newTemplate.subject = email.subject;
      newTemplate.body = email.body;

      return state.purpose.sendEmailTemplate(this.store, this.emailsService, newTemplate, connectorId, state.callerInfo, skill.id, contextData, attachments);
   }

   @Action(EndEmailProcess)
   @ImmutableContext()
   public EndEmailProcess(ctx: StateContext<EmailDialogBehaviourStateModel>, action: EndEmailProcess) {
      ctx.dispatch(new DeleteEmailBehaviourState(action.messageId));
      ctx.setState((state: EmailDialogBehaviourStateModel) => {
         delete state[action.messageId];
         return state;
      });
      ctx.dispatch(new RemoveWindow(WindowTypes.Email, action.messageId));
   }
}
